import React, { useState, useEffect, useRef } from 'react';
import { 
  ChevronRight, ChevronDown, Search, Play, Folder, FolderOpen, 
  Plus, Upload, Link2, Star, StarOff, Edit, Trash2, X, 
  CheckCircle, Clock, Award, Video, Youtube, ExternalLink,
  BarChart3, Filter, Settings, GraduationCap
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api';
import { toast } from 'sonner';

const TrainingVideosPage = () => {
  const [folders, setFolders] = useState([]);
  const [videos, setVideos] = useState([]);
  const [myProgress, setMyProgress] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('User');
  
  // Modals
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Selected items
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [currentCertificate, setCurrentCertificate] = useState(null);
  
  // Form data
  const [videoForm, setVideoForm] = useState({
    title: '', description: '', folderId: '', videoType: 'youtube',
    videoUrl: '', duration: '', allowedRoles: '', hasQuiz: false
  });
  const [folderForm, setFolderForm] = useState({
    name: '', description: '', parentId: '', allowedRoles: ''
  });
  const [uploadFile, setUploadFile] = useState(null);

  const isAdmin = ['Admin', 'Administrator', 'Super Admin'].includes(userRole);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role || 'User');
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [foldersRes, videosRes, progressRes] = await Promise.all([
        api.get('/training/folders'),
        api.get('/training/videos'),
        api.get('/training/my-progress')
      ]);
      
      setFolders(foldersRes.data.folders || []);
      setVideos(videosRes.data.videos || []);
      setMyProgress(progressRes.data);
      
      // Auto-expand first level
      const expanded = {};
      foldersRes.data.folders?.forEach(f => {
        if (!f.parentId) expanded[f.id] = true;
      });
      setExpandedSections(expanded);
    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Failed to load training videos');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const allExpanded = {};
    folders.forEach(f => { allExpanded[f.id] = true; });
    setExpandedSections(allExpanded);
  };

  const collapseAll = () => setExpandedSections({});

  // Filter videos by search
  const filteredVideos = videos.filter(v => 
    v.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get videos for a folder
  const getVideosInFolder = (folderId) => 
    filteredVideos.filter(v => v.folderId === folderId);

  // Get root-level videos
  const getRootVideos = () => filteredVideos.filter(v => !v.folderId);

  // Get child folders
  const getChildFolders = (parentId) => 
    folders.filter(f => f.parentId === parentId);

  // Get root folders
  const getRootFolders = () => folders.filter(f => !f.parentId);

  // Handle bulk import
  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkImportFile);
      
      const res = await api.post('/api/training/videos/bulk-import', formData);
      
      if (res.data.imported > 0) {
        toast.success(`Successfully imported ${res.data.imported} videos`);
      }
      if (res.data.skipped > 0) {
        toast.info(`${res.data.skipped} videos skipped`);
      }
      if (res.data.errors?.length > 0) {
        console.log('Import errors:', res.data.errors);
      }
      
      setShowBulkImport(false);
      setBulkImportFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import videos');
    } finally {
      setImporting(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = 'title,youtube_url,description,duration,allowed_roles\n"Introduction to Home Health","https://www.youtube.com/watch?v=EXAMPLE","Basic overview","10:30","Nurse,Admin"\n"HIPAA Training","https://youtu.be/EXAMPLE2","Required compliance training","45:00","All"';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'training_videos_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle video creation
  const handleCreateVideo = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.entries(videoForm).forEach(([key, value]) => {
        if (value !== '' && value !== null) formData.append(key, value);
      });
      if (uploadFile) formData.append('file', uploadFile);

      await api.post('/training/videos', formData);
      toast.success('Video added successfully!');
      setShowAddVideo(false);
      setVideoForm({ title: '', description: '', folderId: '', videoType: 'youtube', videoUrl: '', duration: '', allowedRoles: '', hasQuiz: false });
      setUploadFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add video');
    }
  };

  // Handle folder creation
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.entries(folderForm).forEach(([key, value]) => {
        if (value !== '' && value !== null) formData.append(key, value);
      });

      await api.post('/training/folders', formData);
      toast.success('Folder created successfully!');
      setShowAddFolder(false);
      setFolderForm({ name: '', description: '', parentId: '', allowedRoles: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  // Handle video deletion
  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    try {
      await api.delete(`/training/videos/${videoId}`);
      toast.success('Video deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  // Handle folder deletion
  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Videos will be moved to root.')) return;
    try {
      await api.delete(`/training/folders/${folderId}`);
      toast.success('Folder deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  // Toggle favorite
  const handleToggleFavorite = async (videoId) => {
    try {
      const res = await api.post(`/training/videos/${videoId}/favorite`);
      setVideos(prev => prev.map(v => 
        v.id === videoId ? { ...v, isFavorite: res.data.isFavorite } : v
      ));
      toast.success(res.data.isFavorite ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  // Play video
  const handlePlayVideo = async (video) => {
    try {
      const res = await api.get(`/training/videos/${video.id}`);
      setSelectedVideo(res.data.video);
      setShowVideoPlayer(true);
    } catch (error) {
      toast.error('Failed to load video');
    }
  };

  // Update progress
  const handleUpdateProgress = async (videoId, watchedSeconds, totalDuration, completed = false) => {
    try {
      const formData = new FormData();
      formData.append('watchedSeconds', watchedSeconds);
      formData.append('totalDuration', totalDuration);
      formData.append('completed', completed);
      await api.post(`/training/videos/${videoId}/progress`, formData);
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  // Get video embed URL
  const getEmbedUrl = (video) => {
    if (video.videoType === 'youtube') {
      const match = video.videoUrl?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      if (match) return `https://www.youtube.com/embed/${match[1]}`;
    } else if (video.videoType === 'vimeo') {
      const match = video.videoUrl?.match(/vimeo\.com\/(\d+)/);
      if (match) return `https://player.vimeo.com/video/${match[1]}`;
    } else if (video.videoType === 'upload' && video.filePath) {
      const stored = localStorage.getItem('ivory_user');
      const token = stored ? JSON.parse(stored).token : localStorage.getItem('token');
      return `${process.env.REACT_APP_BACKEND_URL}${video.filePath}?auth=${encodeURIComponent(token || '')}`;
    }
    return null;
  };

  // Render folder tree recursively
  const renderFolderTree = (parentId = null, depth = 0) => {
    const childFolders = parentId ? getChildFolders(parentId) : getRootFolders();
    const videosInFolder = parentId ? getVideosInFolder(parentId) : getRootVideos();

    return (
      <>
        {childFolders.map(folder => (
          <div key={folder.id} style={{ marginLeft: depth * 20 }} className="mb-1">
            <div className="flex items-center group">
              <button
                onClick={() => toggleSection(folder.id)}
                className="flex items-center gap-2 py-1.5 hover:bg-gray-50 rounded flex-1 text-left"
                data-testid={`folder-${folder.id}`}
              >
                {expandedSections[folder.id] ? (
                  <FolderOpen className="w-5 h-5 text-orange-500" />
                ) : (
                  <Folder className="w-5 h-5 text-orange-500" />
                )}
                <span className="font-medium text-gray-800">{folder.name}</span>
                <span className="text-xs text-gray-400 ml-2">
                  ({getVideosInFolder(folder.id).length} videos)
                </span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => handleDeleteFolder(folder.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {expandedSections[folder.id] && (
              <div className="ml-6 border-l-2 border-orange-200 pl-3">
                {renderFolderTree(folder.id, depth + 1)}
                {getVideosInFolder(folder.id).map(video => renderVideoItem(video))}
              </div>
            )}
          </div>
        ))}
        
        {/* Root level videos (only show at depth 0) */}
        {depth === 0 && videosInFolder.map(video => renderVideoItem(video))}
      </>
    );
  };

  // Render single video item
  const renderVideoItem = (video) => {
    const progress = video.progress;
    const isCompleted = progress?.completed;
    const percentWatched = progress?.percentWatched || 0;

    return (
      <div
        key={video.id}
        className="flex items-center gap-2 py-2 px-2 hover:bg-orange-50 rounded group cursor-pointer"
        onClick={() => handlePlayVideo(video)}
        data-testid={`video-${video.id}`}
      >
        {/* Video type icon */}
        <div className="flex-shrink-0">
          {video.videoType === 'youtube' ? (
            <Youtube className="w-5 h-5 text-red-500" />
          ) : video.videoType === 'vimeo' ? (
            <Video className="w-5 h-5 text-blue-500" />
          ) : (
            <Play className="w-5 h-5 text-green-500" />
          )}
        </div>

        {/* Title and progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 truncate">{video.title}</span>
            {isCompleted && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
            {video.hasQuiz && <GraduationCap className="w-4 h-4 text-purple-500 flex-shrink-0" />}
          </div>
          {percentWatched > 0 && !isCompleted && (
            <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
              <div 
                className="bg-orange-500 h-1 rounded-full" 
                style={{ width: `${Math.min(percentWatched, 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* Duration */}
        {video.duration && (
          <span className="text-xs text-gray-400 flex-shrink-0">
            {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
          </span>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          <button
            onClick={(e) => { e.stopPropagation(); handleToggleFavorite(video.id); }}
            className="p-1 hover:bg-orange-100 rounded"
          >
            {video.isFavorite ? (
              <Star className="w-4 h-4 text-yellow-500 fill-current" />
            ) : (
              <StarOff className="w-4 h-4 text-gray-400" />
            )}
          </button>
          {isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDeleteVideo(video.id); }}
              className="p-1 hover:bg-red-100 rounded text-red-500"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-light text-[#e67e22]">Training Videos</h1>
          {isAdmin && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkImport(true)}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm"
                data-testid="bulk-import-btn"
              >
                <Upload className="w-4 h-4" />
                Bulk Import
              </button>
              <button
                onClick={() => setShowAddFolder(true)}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm"
                data-testid="add-folder-btn"
              >
                <Folder className="w-4 h-4" />
                Add Folder
              </button>
              <button
                onClick={() => setShowAddVideo(true)}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-lg text-sm"
                data-testid="add-video-btn"
              >
                <Plus className="w-4 h-4" />
                Add Video
              </button>
            </div>
          )}
        </div>

        {/* Progress Stats */}
        {myProgress?.stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <Video className="w-4 h-4" />
                Total Videos
              </div>
              <div className="text-2xl font-bold text-gray-800">{myProgress.stats.totalVideos}</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                <CheckCircle className="w-4 h-4" />
                Completed
              </div>
              <div className="text-2xl font-bold text-green-600">{myProgress.stats.completedVideos}</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="flex items-center gap-2 text-orange-500 text-sm mb-1">
                <Clock className="w-4 h-4" />
                In Progress
              </div>
              <div className="text-2xl font-bold text-orange-500">{myProgress.stats.inProgressVideos}</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                <Video className="w-4 h-4" />
                Not Started
              </div>
              <div className="text-2xl font-bold text-gray-400">{myProgress.stats.notStartedVideos}</div>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm border">
              <div className="flex items-center gap-2 text-purple-600 text-sm mb-1">
                <Award className="w-4 h-4" />
                Certificates
              </div>
              <div className="text-2xl font-bold text-purple-600">{myProgress.certificates?.length || 0}</div>
            </div>
          </div>
        )}

        {/* Search and Controls */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
              data-testid="video-search"
            />
          </div>
          <button
            onClick={expandAll}
            className="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            <ChevronDown className="w-4 h-4" />
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg text-sm"
          >
            <ChevronRight className="w-4 h-4" />
            Collapse All
          </button>
        </div>

        {/* Video Tree */}
        <div className="bg-white rounded-lg shadow-sm border p-4 min-h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
            </div>
          ) : folders.length === 0 && videos.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <Video className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No training videos yet</p>
              {isAdmin && (
                <p className="text-sm mt-2">Click "Add Video" to upload your first training video</p>
              )}
            </div>
          ) : (
            renderFolderTree()
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8 pt-4 border-t">
          © 2026 Ivory Healthcare Systems, LLC. <span className="float-right">Version: 8.61.4.19</span>
        </div>
      </div>

      {/* Add Video Modal */}
      {showAddVideo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Add Training Video</h2>
                <button onClick={() => setShowAddVideo(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateVideo} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    required
                    value={videoForm.title}
                    onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="e.g., Alora Training 1 - Activation"
                    data-testid="video-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={videoForm.description}
                    onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                    rows={2}
                    placeholder="Brief description of the video content"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Folder</label>
                  <select
                    value={videoForm.folderId}
                    onChange={(e) => setVideoForm({ ...videoForm, folderId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                  >
                    <option value="">Root Level (No Folder)</option>
                    {folders.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Video Type *</label>
                  <div className="flex gap-2">
                    {['youtube', 'vimeo', 'upload'].map(type => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setVideoForm({ ...videoForm, videoType: type })}
                        className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                          videoForm.videoType === type
                            ? 'bg-orange-500 text-white border-orange-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {type === 'youtube' && <Youtube className="w-4 h-4 inline mr-1" />}
                        {type === 'vimeo' && <Video className="w-4 h-4 inline mr-1" />}
                        {type === 'upload' && <Upload className="w-4 h-4 inline mr-1" />}
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {videoForm.videoType !== 'upload' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {videoForm.videoType === 'youtube' ? 'YouTube' : 'Vimeo'} URL *
                    </label>
                    <input
                      type="url"
                      required
                      value={videoForm.videoUrl}
                      onChange={(e) => setVideoForm({ ...videoForm, videoUrl: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder={videoForm.videoType === 'youtube' 
                        ? 'https://www.youtube.com/watch?v=...' 
                        : 'https://vimeo.com/...'}
                      data-testid="video-url-input"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload Video *</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        className="hidden"
                        id="video-upload"
                      />
                      <label htmlFor="video-upload" className="cursor-pointer">
                        <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          {uploadFile ? uploadFile.name : 'Click to upload or drag and drop'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">MP4, WebM, MOV up to 500MB</p>
                      </label>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration (seconds)</label>
                    <input
                      type="number"
                      value={videoForm.duration}
                      onChange={(e) => setVideoForm({ ...videoForm, duration: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder="e.g., 300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Roles</label>
                    <input
                      type="text"
                      value={videoForm.allowedRoles}
                      onChange={(e) => setVideoForm({ ...videoForm, allowedRoles: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                      placeholder="Nurse, Admin (comma-separated)"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddVideo(false)}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium"
                    data-testid="save-video-btn"
                  >
                    Add Video
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add Folder Modal */}
      {showAddFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Create Folder</h2>
                <button onClick={() => setShowAddFolder(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateFolder} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Folder Name *</label>
                  <input
                    type="text"
                    required
                    value={folderForm.name}
                    onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="e.g., New User Training"
                    data-testid="folder-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={folderForm.description}
                    onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Folder</label>
                  <select
                    value={folderForm.parentId}
                    onChange={(e) => setFolderForm({ ...folderForm, parentId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">Root Level</option>
                    {folders.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddFolder(false)}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium"
                    data-testid="save-folder-btn"
                  >
                    Create Folder
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {showVideoPlayer && selectedVideo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">{selectedVideo.title}</h2>
                {selectedVideo.description && (
                  <p className="text-sm text-gray-500">{selectedVideo.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleFavorite(selectedVideo.id)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  {selectedVideo.isFavorite ? (
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  ) : (
                    <StarOff className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={() => { setShowVideoPlayer(false); setSelectedVideo(null); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="aspect-video bg-black">
              {selectedVideo.videoType === 'upload' ? (
                <video
                  controls
                  className="w-full h-full"
                  onTimeUpdate={(e) => {
                    const video = e.target;
                    if (video.currentTime % 10 < 1) {
                      handleUpdateProgress(
                        selectedVideo.id,
                        Math.floor(video.currentTime),
                        Math.floor(video.duration)
                      );
                    }
                  }}
                  onEnded={() => {
                    handleUpdateProgress(selectedVideo.id, selectedVideo.duration || 0, selectedVideo.duration || 0, true);
                    toast.success('Video completed!');
                    fetchData();
                  }}
                >
                  <source src={getEmbedUrl(selectedVideo)} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <iframe
                  src={getEmbedUrl(selectedVideo)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedVideo.title}
                ></iframe>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Video className="w-4 h-4" />
                  {selectedVideo.videoType.charAt(0).toUpperCase() + selectedVideo.videoType.slice(1)}
                </span>
                {selectedVideo.duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {Math.floor(selectedVideo.duration / 60)}:{(selectedVideo.duration % 60).toString().padStart(2, '0')}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  {selectedVideo.viewCount} views
                </span>
              </div>
              
              <div className="flex gap-2">
                {selectedVideo.videoUrl && (
                  <a
                    href={selectedVideo.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in new tab
                  </a>
                )}
                <button
                  onClick={() => {
                    handleUpdateProgress(selectedVideo.id, selectedVideo.duration || 100, selectedVideo.duration || 100, true);
                    toast.success('Marked as completed!');
                    setShowVideoPlayer(false);
                    fetchData();
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Bulk Import Training Videos</h2>
                <button onClick={() => setShowBulkImport(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-medium text-blue-800 mb-2">CSV Format</h3>
                  <p className="text-sm text-blue-700 mb-3">
                    Upload a CSV file with the following columns:
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li><strong>title</strong> - Video title (required)</li>
                    <li><strong>youtube_url</strong> - YouTube video URL (required)</li>
                    <li><strong>description</strong> - Video description</li>
                    <li><strong>duration</strong> - Duration in MM:SS format</li>
                    <li><strong>allowed_roles</strong> - Comma-separated roles</li>
                  </ul>
                </div>

                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Download CSV Template
                </button>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setBulkImportFile(e.target.files[0])}
                    className="hidden"
                    id="bulk-csv-upload"
                  />
                  <label htmlFor="bulk-csv-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      {bulkImportFile ? bulkImportFile.name : 'Click to upload CSV file'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">CSV files only</p>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowBulkImport(false); setBulkImportFile(null); }}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkImport}
                    disabled={!bulkImportFile || importing}
                    className="flex-1 py-2 px-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium disabled:opacity-50"
                    data-testid="import-videos-btn"
                  >
                    {importing ? (
                      <>
                        <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
                        Importing...
                      </>
                    ) : (
                      'Import Videos'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default TrainingVideosPage;
